import { IP_API_BASE_URL } from '@/core/constants/appConfig/environment';
import { IpData } from '@/core/data_handlers/ip_data/types';
import { TimeUtils } from '@/core/event_logic/utils/time_utils';
import { callAPI } from '@/shared/fetch_client';
import { Logger } from '@/shared/logger/logger';

export type TIpApiResponse = {
  ip_address: {
    v4: string;
    v6: string;
  };
  ip_location: {
    cc: string;
    ct: string;
    rc: string;
    rg: string;
    zip: string;
  };
  ua_headers: Record<string, string>;
};

export const IpApi = {
  // Helper function to classify an IP address as v4 or v6
  classifyIpAddress(ip: string): { v4: string; v6: string } {
    return Logger.profileFn('IpApi:classifyIpAddress', () => {
      if (!ip) {
        return { v4: '', v6: '' };
      }

      if (IpApi.IPV4_REGEX.test(ip)) {
        return { v4: ip, v6: '' };
      }

      if (IpApi.IPV6_REGEX.test(ip)) {
        return { v4: '', v6: ip };
      }

      // If not matching either format, don't assume it's an IP
      return { v4: '', v6: '' };
    });
  },
  async get(): Promise<IpData> {
    return Logger.profileFnAsync('IpApi:get', async () => {
      let data: TIpApiResponse;
      try {
        const response = await callAPI<TIpApiResponse>({
          method: 'GET',
          url: IP_API_BASE_URL,
        });
        data = response.data;
        if (!data) {
          throw new Error('No data returned');
        }
      } catch (error) {
        Logger.error('Error fetching IP data from API', error);
        data = {
          ip_address: { v4: '', v6: '' },
          ip_location: { cc: '', ct: '', rc: '', rg: '', zip: '' },
          ua_headers: {},
        };
      }

      // Extract and safely handle all properties
      let ipAddress = {
        v4: data.ip_address?.v4 ?? '',
        v6: data.ip_address?.v6 ?? '',
      };

      const ipLocation = {
        cc: data.ip_location?.cc ?? '',
        ct: data.ip_location?.ct ?? '',
        rc: data.ip_location?.rc ?? '',
        rg: data.ip_location?.rg ?? '',
        zip: data.ip_location?.zip ? String(data.ip_location.zip) : '',
      };

      // Try to get IP info from time headers as fallback
      if (!ipAddress.v4 && !ipAddress.v6) {
        try {
          const timeIpData = await IpApi.getIpFromTimeHeaders();
          if (timeIpData) {
            Logger.debug('Using IP data from time headers as fallback');
            return {
              ipAddress: timeIpData.ipAddress,
              ipLocation,
              uaHeaders: timeIpData.uaHeaders,
            };
          } else throw new Error('IP data from fallback headers are empty');
        } catch (error) {
          Logger.debug('Failed to get IP address from fallback', error);
        }
      }

      // If we got a successful response, return it
      return {
        ipAddress,
        ipLocation,
        uaHeaders: data.ua_headers ?? {},
      };
    });
  },

  // Get IP info from time API headers as fallback
  async getIpFromTimeHeaders(): Promise<{
    ipAddress: { v4: string; v6: string };
    uaHeaders: Record<string, string>;
  } | null> {
    return Logger.profileFnAsync('IpApi:getIpFromTimeHeaders', async () => {
      try {
        const timeInfo = await TimeUtils.getCurrentSyncedTime();
        if (!timeInfo?.headers) {
          return null;
        }

        const headers = timeInfo.headers;
        let ipv4 = headers['x-client-ip-v4'] || '';
        let ipv6 = headers['x-client-ip-v6'] || '';

        // If specific v4/v6 headers aren't available, try to classify the general IP
        if (!ipv4 && !ipv6) {
          const clientIp =
            headers['x-client-ip'] ||
            headers['x-real-ip'] ||
            headers['x-forwarded-for'] ||
            '';
          if (clientIp) {
            // Handle multiple IPs in X-Forwarded-For (take the first one)
            const firstIp = clientIp.split(',')[0]?.trim();
            if (firstIp) {
              const classified = IpApi.classifyIpAddress(firstIp);
              ipv4 = classified.v4;
              ipv6 = classified.v6;
            }
          }
        }

        return {
          ipAddress: { v4: ipv4, v6: ipv6 },
          uaHeaders: headers,
        };
      } catch (error) {
        Logger.debug('Error getting IP from time headers', error);
        return null;
      }
    });
  },

  // Regular expressions for validating IP addresses
  IPV4_REGEX:
    /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d{1,2})\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d{1,2})$/,

  IPV6_REGEX:
    /^(([\dA-Fa-f]{1,4}:){7}[\dA-Fa-f]{1,4}|([\dA-Fa-f]{1,4}:){1,7}:|([\dA-Fa-f]{1,4}:){1,6}:[\dA-Fa-f]{1,4}|([\dA-Fa-f]{1,4}:){1,5}(:[\dA-Fa-f]{1,4}){1,2}|([\dA-Fa-f]{1,4}:){1,4}(:[\dA-Fa-f]{1,4}){1,3}|([\dA-Fa-f]{1,4}:){1,3}(:[\dA-Fa-f]{1,4}){1,4}|([\dA-Fa-f]{1,4}:){1,2}(:[\dA-Fa-f]{1,4}){1,5}|[\dA-Fa-f]{1,4}:((:[\dA-Fa-f]{1,4}){1,6})|:((:[\dA-Fa-f]{1,4}){1,7}|:)|fe80:(:[\dA-Fa-f]{0,4}){0,4}%[\dA-Za-z]+|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}\d){0,1}\d)\.){3}(25[0-5]|(2[0-4]|1{0,1}\d){0,1}\d)|([\dA-Fa-f]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}\d){0,1}\d)\.){3}(25[0-5]|(2[0-4]|1{0,1}\d){0,1}\d))$/,
};
